import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { queryKeys } from "@/lib/queryKeys";
import posthog from "posthog-js";

export interface HomeCounts {
  total: number;
  /** Cafés inside Los Angeles County, Orange County, San Diego County or Long Beach. */
  southern_california: number;
  los_angeles: number;
  orange_county: number;
  san_diego: number;
  long_beach: number;
}

const STORAGE_KEY = "grounded_home_counts";
const DAY = 24 * 60 * 60 * 1000;
/** Hard expiry. A number on the page is never older than this. */
export const COUNTS_MAX_AGE_MS = 7 * DAY;

interface Mirror {
  t: number;
  v: HomeCounts;
}

function isCounts(v: unknown): v is HomeCounts {
  if (!v || typeof v !== "object") return false;
  const c = v as Record<string, unknown>;
  return (
    [
      "total",
      "southern_california",
      "los_angeles",
      "orange_county",
      "san_diego",
      "long_beach",
    ] as const
  ).every((k) => typeof c[k] === "number" && Number.isFinite(c[k] as number));
}

export function readMirror(now = Date.now()): HomeCounts | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Mirror;
    if (!parsed || typeof parsed.t !== "number" || !isCounts(parsed.v)) return null;
    // Expired cache is discarded rather than rendered as a stale figure.
    if (now - parsed.t > COUNTS_MAX_AGE_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed.v;
  } catch {
    return null;
  }
}

function writeMirror(v: HomeCounts) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ t: Date.now(), v } satisfies Mirror));
  } catch {
    /* storage full or blocked: the page simply refetches next visit */
  }
}

/** Rounded DOWN to the nearest 50, e.g. 981 becomes 950. */
export function roundedTotal(total: number): number | null {
  if (!Number.isFinite(total) || total < 50) return null;
  return Math.floor(total / 50) * 50;
}

/**
 * Live café counts for the public homepage.
 *
 * No hardcoded fallback figures anywhere: if the RPC fails and the localStorage
 * mirror is missing or older than 7 days, `counts` is null and the copy degrades
 * to a numberless variant rather than printing a fabricated number.
 */
export function useHomeCounts() {
  const seeded = useMemo(() => readMirror(), []);

  const query = useQuery({
    queryKey: queryKeys.homeCounts(),
    queryFn: async (): Promise<HomeCounts> => {
      const { data, error } = await supabase.rpc("get_homepage_counts");
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      const parsed = {
        total: Number(row?.total ?? 0),
        southern_california: Number(row?.southern_california ?? 0),
        los_angeles: Number(row?.los_angeles ?? 0),
        orange_county: Number(row?.orange_county ?? 0),
        san_diego: Number(row?.san_diego ?? 0),
        long_beach: Number(row?.long_beach ?? 0),
      };
      if (!isCounts(parsed) || parsed.total <= 0) throw new Error("empty homepage counts");
      writeMirror(parsed);
      return parsed;
    },
    initialData: seeded ?? undefined,
    staleTime: DAY,
    gcTime: COUNTS_MAX_AGE_MS,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const counts = query.data ?? null;
  const failed = !counts && !query.isPending;

  useEffect(() => {
    if (!failed) return;
    try {
      posthog.capture("home_counts_unavailable");
    } catch {
      /* analytics must never break the page */
    }
  }, [failed]);

  return {
    counts,
    /** Settled with no usable number: render the numberless copy. */
    unavailable: failed,
    /** True once the query has settled either way (prerender readiness). */
    settled: !query.isPending,
  };
}
